import React, { useState } from 'react'
import { BarChart3, CheckCircle2, AlertTriangle, ChevronUp, ChevronDown, Loader2, Check, X, FileQuestion, FileText, FileSpreadsheet, Download, Maximize2, Minimize2, Lock, ArrowRight } from 'lucide-react'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedAnalysis } from '../../../store/selectors'
import type { Note } from '../../../store/slices/notesSlice'

interface NotesPanelProps {
  activeTab: 'summary' | 'metrics' | 'verification' | 'validation' | 'notes' | 'custom-calculations'
  notes: Note[]
  activityLog: string[]
  collapsed?: boolean
  onTabChange: (tab: 'summary' | 'metrics' | 'verification' | 'validation' | 'notes' | 'custom-calculations') => void
  onAddNote: (content: string) => void
  onEditNote: (id: string, content: string) => void
  onDeleteNote: (id: string) => void
  onNoteClick?: (note: Note) => void
  onToggleCollapse?: () => void
}

const NotesPanel: React.FC<NotesPanelProps> = ({
  activeTab,
  notes,
  //activityLog,
  collapsed = false,
  onTabChange,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onNoteClick,
  onToggleCollapse
}) => {
  const analysis = useAppSelector(selectSelectedAnalysis)
  const workItem = useAppSelector(state => 
    analysis?.workId ? state.work.byId[analysis.workId] : undefined
  )
  const [panelHeight, setPanelHeight] = useState(256) // Default 16rem = 256px
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartY, setDragStartY] = useState(0)
  const [dragStartHeight, setDragStartHeight] = useState(0)
  const [isMaximized, setIsMaximized] = useState(false)
  const [previousHeight, setPreviousHeight] = useState(256)

  // Handle panel resizing
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
    setDragStartY(e.clientY)
    setDragStartHeight(panelHeight)
  }

  const handleResizeMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging) return
    
    const deltaY = dragStartY - e.clientY // Negative delta means dragging up
    const newHeight = Math.max(120, Math.min(600, dragStartHeight + deltaY)) // Min 120px, max 600px
    setPanelHeight(newHeight)
  }, [isDragging, dragStartY, dragStartHeight])

  const handleResizeEnd = React.useCallback(() => {
    setIsDragging(false)
  }, [])

  // Set up resize listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
      }
    }
  }, [isDragging, handleResizeMove, handleResizeEnd])

  // Handle maximize/minimize toggle
  const handleToggleMaximize = () => {
    if (isMaximized) {
      // Restore to previous height
      setPanelHeight(previousHeight)
      setIsMaximized(false)
    } else {
      // Save current height and maximize
      setPreviousHeight(panelHeight)
      setIsMaximized(true)
    }
  }

  // Calculate the actual height to use
  const effectiveHeight = isMaximized ? 'calc(100vh - 140px)' : `${panelHeight}px`

  return (
    <div 
      className="bg-gray-50 border-t border-gray-200 flex flex-col transition-all duration-200 relative"
      style={{ 
        height: collapsed ? '40px' : effectiveHeight,
        minHeight: collapsed ? '40px' : '120px',
        maxHeight: collapsed ? '40px' : (isMaximized ? 'calc(100vh - 140px)' : '600px')
      }}
    >
      {/* Resize Handle */}
      {!collapsed && (
        <div
          className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-orange-400 transition-colors z-10 group"
          onMouseDown={handleResizeStart}
          title="Drag to resize panel"
        >
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-300 group-hover:bg-orange-400 rounded-full transition-colors" />
        </div>
      )}
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200 items-center bg-white">
        {/* Summary Tab */}
        <button
          onClick={() => onTabChange('summary')}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-colors ${
            activeTab === 'summary'
              ? 'bg-white border-b-2 border-orange-500 text-orange-600'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <FileText className="w-4 h-4" />
          {!collapsed && <span>Summary</span>}
        </button>
        
        {/* Metrics Tab */}
        <button
          onClick={() => onTabChange('metrics')}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-colors ${
            activeTab === 'metrics'
              ? 'bg-white border-b-2 border-orange-500 text-orange-600'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          {!collapsed && <span>Key Metrics</span>}
        </button>
        
        {/* Verification Tab */}
        <button
          onClick={() => onTabChange('verification')}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-colors ${
            activeTab === 'verification'
              ? 'bg-white border-b-2 border-orange-500 text-orange-600'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          {!collapsed && <span>Verification</span>}
        </button>
        
        {/* Validation Tab */}
        <button
          onClick={() => onTabChange('validation')}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-colors ${
            activeTab === 'validation'
              ? 'bg-white border-b-2 border-orange-500 text-orange-600'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          {!collapsed && <span>Validation</span>}
        </button>
        
        {/* Custom Calculations Tab - Locked Enterprise Feature */}
        <button
          onClick={() => onTabChange('custom-calculations')}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-colors relative group ${
            activeTab === 'custom-calculations'
              ? 'bg-white border-b-2 border-orange-500 text-orange-600'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
          }`}
          title="Our enterprise version of Extract enables customers to set up custom rules, validations, and calculations. For instance, you might need to calculate negative balance days or perform a cash flow analysis. If you're interested in this feature, reach out to sales@fenero.ai, and we'll get you set up."
        >
          <Lock className="w-4 h-4" />
          {!collapsed && <span className="opacity-60">Custom Calculations</span>}
        </button>
        
        <div className="flex-1" />
        {!collapsed && (
          <button
            onClick={handleToggleMaximize}
            className="p-2 hover:bg-gray-100 transition-colors"
            title={isMaximized ? 'Restore Panel Size' : 'Maximize Panel'}
          >
            {isMaximized ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-2 hover:bg-gray-100 transition-colors mr-2"
            title={collapsed ? 'Expand Panel' : 'Collapse Panel'}
          >
            {collapsed ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Tab Content */}
      {!collapsed && (
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 bg-white">
        {/* No Document Selected State */}
        {!analysis && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <FileQuestion className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Document Selected</h3>
            <p className="text-sm text-gray-600 max-w-md">
              Select a document from the sidebar to view its {
                activeTab === 'summary' ? 'analysis summary' :
                activeTab === 'metrics' ? 'key metrics' :
                activeTab === 'verification' ? 'verification data' :
                activeTab === 'validation' ? 'validation results' :
                'information'
              }.
            </p>
          </div>
        )}
        
        {/* Processing State */}
        {analysis && analysis.status === 'pending' && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Processing Document</h3>
            <p className="text-sm text-gray-600 max-w-md">
              Document analysis is in progress. Metrics, verification, and validation data will appear here once extraction is complete.
            </p>
          </div>
        )}
        
        {/* Error State */}
        {analysis && analysis.status === 'error' && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Analysis Error</h3>
            <p className="text-sm text-gray-600 max-w-md">
              {analysis.error || 'An error occurred during document analysis'}
            </p>
          </div>
        )}
        
        {/* Complete State - Show Data */}
        {analysis && analysis.status === 'complete' && (
          <>
            {activeTab === 'summary' && (
              <div className="space-y-4">
                {/* Overall Status Card */}
                <div className={`rounded-lg p-4 border-2 ${
                  analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR')
                    ? 'bg-red-50 border-red-200'
                    : analysis.validation.some(v => v.status === 'WARNING')
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR') ? (
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : analysis.validation.some(v => v.status === 'WARNING') ? (
                      <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR')
                          ? 'text-red-900'
                          : analysis.validation.some(v => v.status === 'WARNING')
                          ? 'text-yellow-900'
                          : 'text-green-900'
                      }`}>
                        {analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR')
                          ? 'Validation Issues Found'
                          : analysis.validation.some(v => v.status === 'WARNING')
                          ? 'Extraction Complete with Warnings'
                          : 'Extraction Successful'
                        }
                      </h3>
                      <p className={`text-sm ${
                        analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR')
                          ? 'text-red-700'
                          : analysis.validation.some(v => v.status === 'WARNING')
                          ? 'text-yellow-700'
                          : 'text-green-700'
                      }`}>
                        {analysis.validation.some(v => v.status === 'FAILED' || v.status === 'ERROR')
                          ? 'The document was extracted but some validation checks failed. Review the issues below.'
                          : analysis.validation.some(v => v.status === 'WARNING')
                          ? 'The document was successfully extracted with minor warnings. Review the details below.'
                          : 'The document passed all validation checks and is ready for use.'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Excel Download Button */}
                {workItem?.outputs?.excelUrl && (
                  <a
                    href={workItem.outputs.excelUrl}
                    download={`${workItem.subject?.fileName?.replace('.pdf', '') || 'document'}.xlsx`}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-sm transition-colors w-full"
                    title="Download Excel file with extracted data"
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    <span>Download Excel Report</span>
                    <Download className="w-4 h-4" />
                  </a>
                )}

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {analysis.keyMetrics.map((metric, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">{metric.metric}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {typeof metric.value === 'boolean' ? (
                          <div className="flex items-center gap-1">
                            {metric.value ? (
                              <>
                                <Check className="w-5 h-5 text-green-600" />
                                <span className="text-sm text-green-700">Yes</span>
                              </>
                            ) : (
                              <>
                                <X className="w-5 h-5 text-red-600" />
                                <span className="text-sm text-red-700">No</span>
                              </>
                            )}
                          </div>
                        ) : (
                          String(metric.value)
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Issues & Warnings */}
                {(analysis.validation.some(v => v.errors.length > 0 || v.warnings.length > 0)) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Issues Detected</h4>
                    
                    {analysis.validation.filter(v => v.errors.length > 0).map((item, idx) => (
                      <div key={`error-${idx}`} className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-red-900">{item.category}</div>
                            <ul className="mt-1 space-y-1">
                              {item.errors.map((error, eidx) => (
                                <li key={eidx} className="text-sm text-red-700">• {error}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {analysis.validation.filter(v => v.warnings.length > 0).map((item, idx) => (
                      <div key={`warning-${idx}`} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-yellow-900">{item.category}</div>
                            <ul className="mt-1 space-y-1">
                              {item.warnings.map((warning, widx) => (
                                <li key={widx} className="text-sm text-yellow-700">• {warning}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Data Quality Overview */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Data Quality Overview</h4>
                  <div className="space-y-2">
                    {analysis.verification.map((item, idx) => {
                      const isExcellent = item.correctness === 100
                      const isGood = item.correctness >= 95
                      const isFair = item.correctness >= 80
                      
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{item.category}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${
                                  isExcellent ? 'bg-green-500' : 
                                  isGood ? 'bg-blue-500' : 
                                  isFair ? 'bg-yellow-500' : 
                                  'bg-red-500'
                                }`}
                                style={{ width: `${item.correctness}%` }}
                              />
                            </div>
                            <span className={`font-medium min-w-[3rem] text-right ${
                              isExcellent ? 'text-green-600' : 
                              isGood ? 'text-blue-600' : 
                              isFair ? 'text-yellow-600' : 
                              'text-red-600'
                            }`}>
                              {item.correctness.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Explanation Card */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Understanding Your Results</h4>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p>
                      <strong>Data Quality:</strong> Shows how accurately each type of information was extracted. 100% means perfect extraction.
                    </p>
                    <p>
                      <strong>Validation Status:</strong> Indicates whether the extracted data passed our quality checks. "PASSED" means all checks succeeded.
                    </p>
                    <p>
                      <strong>Balance Calculation Match:</strong> Compares the sum of transactions against the reported beginning and ending balances. A high percentage means the transactions accurately account for the balance change.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'metrics' && (
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Metric
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analysis.keyMetrics.map((metric, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-700">{metric.metric}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {typeof metric.value === 'boolean' ? (
                              metric.value ? (
                                <Check className="w-5 h-5 text-green-600" />
                              ) : (
                                <X className="w-5 h-5 text-red-600" />
                              )
                            ) : (
                              String(metric.value)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {activeTab === 'verification' && (
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Correctness
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analysis.verification.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-700">{item.category}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                                <div 
                                  className={`h-2 rounded-full transition-all ${
                                    item.correctness === 100 ? 'bg-green-500' : 
                                    item.correctness >= 75 ? 'bg-yellow-500' : 
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${item.correctness}%` }}
                                />
                              </div>
                              <span className={`text-sm font-semibold min-w-[4rem] ${
                                item.correctness === 100 ? 'text-green-600' : 
                                item.correctness >= 75 ? 'text-yellow-600' : 
                                'text-red-600'
                              }`}>
                                {item.correctness.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {activeTab === 'validation' && (
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Warnings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Errors
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analysis.validation.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-700">{item.category}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.status === 'PASSED' ? 'bg-green-100 text-green-800' :
                              item.status === 'WARNING' ? 'bg-yellow-100 text-yellow-800' :
                              item.status === 'FAILED' || item.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {item.warnings && item.warnings.length > 0 ? item.warnings.join(', ') : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {item.errors && item.errors.length > 0 ? item.errors.join(', ') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {activeTab === 'custom-calculations' && (
              <div className="relative h-full">
                {/* Blurred Background Content */}
                <div className="blur-sm pointer-events-none select-none h-full">
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Calculation
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Result
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 text-sm text-gray-700">Negative Balance Days</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">3 days</td>
                            <td className="px-6 py-4"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Review</span></td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 text-sm text-gray-700">Cash Flow Analysis</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">$12,450.00</td>
                            <td className="px-6 py-4"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Passed</span></td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 text-sm text-gray-700">Average Daily Balance</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">$8,234.56</td>
                            <td className="px-6 py-4"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Passed</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                {/* Overlay with Enterprise Message */}
                <div className="absolute inset-0 flex items-center justify-center overflow-y-auto bg-white/60 backdrop-blur-[2px] p-4">
                  <div className="max-w-md w-full text-center my-auto">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                      <Lock className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Feature</h3>
                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                      Our enterprise version of Extract enables customers to set up custom rules, validations, and calculations. For instance, you might need to calculate negative balance days or perform a cash flow analysis.
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      If you're interested in this feature, reach out to{' '}
                      <a href="mailto:sales@fenero.ai" className="font-semibold text-orange-600 hover:text-orange-700 underline">
                        sales@fenero.ai
                      </a>
                      , and we'll get you set up.
                    </p>
                    <a 
                      href="mailto:sales@fenero.ai?subject=Enterprise%20Extract%20-%20Custom%20Calculations"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold shadow-md transition-colors"
                    >
                      Contact Sales
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  )
}

export default NotesPanel
